import { LitElement, css, html } from 'lit';
import { state, query, property } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
//import { AKIT_STATE, AKIT_StateGet } from './app3/akit/state/akit-StateSystemConfig';

import '@shoelace-style/shoelace/dist/themes/light.css';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/drawer/drawer.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/qr-code/qr-code.js';
import { registerIconLibrary } from '@shoelace-style/shoelace/dist/components/icon/library.js';

// Register the default icon library
registerIconLibrary('default', {
  resolver: name => `/assets/icons/${name}.svg`,
  mutator: svg => svg.setAttribute('fill', 'currentColor'),
});

interface XRState {
  AR: boolean;
  VR: boolean;
}

import './styles/global.css';

let instance: LitElement | null = null;

if (!customElements.get('app-index')) {
  class AppIndexClass extends LitElement {
    private static instanceCount = 0;
    private static domInstanceCount = 0;

    static styles = css`
      :host {
        display: block;
        height: 100vh;
        width: 100vw;
        position: absolute;
        overflow: hidden;
        touch-action: none;
        background-color: transparent;
      }

      canvas {
        position: fixed;
        inset: 0;
        z-index: var(--sl-z-index-negative);
      }

      .view-container {
        position: fixed;
        inset: 0;
        z-index: var(--sl-z-index-drawer);
        background-color: transparent;
      }

      .loader {
        position: fixed;
        inset: 0;
        z-index: var(--sl-z-index-toast);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--sl-spacing-medium);
        background-color: var(--sl-color-neutral-900);
      }

      sl-drawer {
        --size: 100%;
        --background-color: var(--sl-color-neutral-900);
        --border-color: var(--sl-color-neutral-800);
        --overlay-color: var(--sl-color-neutral-900);
        --panel-background-color: var(--sl-color-neutral-900);
        --header-background-color: var(--sl-color-neutral-900);
        --header-border-color: var(--sl-color-neutral-800);
        --body-background-color: var(--sl-color-neutral-900);
        --header-padding: var(--sl-spacing-medium);
        --body-padding: 0;
        z-index: var(--sl-z-index-drawer);
        overflow: hidden;
      }

      sl-drawer::part(header) {
        background-color: var(--sl-color-neutral-900);
        border-bottom: none;
      }

      sl-drawer::part(body) {
        padding: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100vh;
        background-color: var(--sl-color-neutral-900);
        overflow: hidden;
      }

      sl-drawer::part(overlay) {
        background-color: var(--sl-color-neutral-900);
      }

      sl-card {
        --padding: 0;
        --background-color: var(--sl-color-neutral-900);
        --border-color: var(--sl-color-neutral-800);
        --header-background-color: var(--sl-color-neutral-900);
        --header-border-color: var(--sl-color-neutral-800);
        --body-background-color: var(--sl-color-neutral-900);
        width: 100%;
        height: 100%;
        z-index: var(--sl-z-index-drawer);
      }

      sl-card::part(header) {
        background-color: var(--sl-color-neutral-900);
        border-bottom: 1px solid var(--sl-color-neutral-800);
        padding: var(--sl-spacing-medium);
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      sl-card::part(body) {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        background-color: var(--sl-color-neutral-900);
        padding: 0;
        overflow: hidden;
      }

      sl-qr-code {
        --size: 150px;
        --background-color: var(--sl-color-neutral-900);
        --foreground-color: var(--sl-color-neutral-0);
        display: block;
        margin: 0 auto;
        error-correction: "H";
        padding: 0;
      }

      sl-text {
        color: #8B4513;
        text-align: center;
        font-weight: var(--sl-font-weight-semibold);
        letter-spacing: 0.05em;
      }

      sl-button {
        --background: transparent;
        --background-hover: transparent;
        --background-active: transparent;
        --background-focus: transparent;
        --border-color: #8B4513;
        --color: #8B4513;
        --box-shadow: none;
        --border-width: 1px;
        --border-radius: 50%;
        --padding: var(--sl-spacing-medium);
        --font-size: var(--sl-font-size-large);
        --border-color-hover: #8B4513;
        --border-color-active: #8B4513;
        --border-color-focus: #8B4513;
        --color-hover: #8B4513;
        --color-active: #8B4513;
        --color-focus: #8B4513;
      }

      sl-button::part(base) {
        background: transparent;
        border-color: #8B4513;
        color: #8B4513;
      }

      sl-button::part(base):hover {
        background: transparent;
        border-color: #8B4513;
        color: #8B4513;
        opacity: 0.8;
      }

      sl-button::part(base):active {
        background: transparent;
        border-color: #8B4513;
        color: #8B4513;
      }

      sl-button::part(base):focus {
        background: transparent;
        border-color: #8B4513;
        color: #8B4513;
      }

      sl-icon-button {
        --background-color: transparent;
        --color: #8B4513;
        --background-hover: transparent;
        --background-active: transparent;
        --background-focus: transparent;
      }

      sl-icon-button:hover {
        --background-color: transparent;
        --color: #8B4513;
        opacity: 0.8;
      }

      sl-icon-button[name="x-lg"] {
        --color: #8B4513;
        --background-color: transparent;
        --background-hover: transparent;
        --background-active: transparent;
        --background-focus: transparent;
      }

      sl-icon-button[name="x-lg"]:hover {
        --color: #8B4513;
        --background-color: transparent;
        --background-hover: transparent;
        --background-active: transparent;
        --background-focus: transparent;
        opacity: 0.8;
      }

      sl-icon-button[name="x-lg"]:active {
        --background-color: transparent;
        --background-hover: transparent;
        --background-active: transparent;
        --background-focus: transparent;
      }

      sl-icon-button[name="x-lg"]:focus {
        --background-color: transparent;
        --background-hover: transparent;
        --background-active: transparent;
        --background-focus: transparent;
      }

      sl-progress-bar {
        --height: 1px;
        --indicator-color: var(--sl-color-neutral-700);
        --track-color: transparent;
        width: 100%;
        max-width: 400px;
      }

      sl-progress-bar::part(indicator) {
        background-color: rgba(0, 0, 0, 0.4);
      }

      sl-spinner {
        --indicator-color: var(--sl-color-neutral-0);
      }

      .header {
        position: fixed;
        inset: 0 0 auto 0;
        z-index: var(--sl-z-index-drawer);
        padding: var(--sl-spacing-medium);
        padding-top: max(var(--sl-spacing-medium), var(--safe-area-inset-top), var(--android-safe-area-top));
      }

      .footer {
        position: fixed;
        inset: auto 0 0 0;
        z-index: var(--sl-z-index-drawer);
        padding: var(--sl-spacing-medium);
        padding-bottom: max(var(--sl-spacing-2x-large), var(--safe-area-inset-bottom), var(--android-safe-area-bottom));
      }

      .drawer-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        gap: var(--sl-spacing-medium);
        overflow: hidden;
      }

      sl-text[size="x-large"] {
        margin-bottom: var(--sl-spacing-small);
      }

      sl-qr-code {
        --size: 200px;
        --background-color: var(--sl-color-neutral-900);
        --foreground-color: #8B4513;
        display: block;
        margin: 0 auto;
        error-correction: "H";
      }
    `;

    @query('canvas')
    private canvas!: HTMLCanvasElement;

    @query('sl-drawer')
    private drawer!: any;

    @state()
    private currentView: 'threed' | 'ar' | 'vr' | 'loading' = 'loading';

    @state()
    private contentPromise: Promise<void> = this.loadContent();

    @state()
    private xrButtonState: XRState = { AR: false, VR: false };

    @state()
    private xrModeState: XRState = { AR: false, VR: false };

    private toggleDrawer() {
      if (this.drawer) {
        if (this.drawer.open) {
          this.drawer.hide();
        } else {
          this.drawer.show();
        }
      }
    }

    async loadContent() {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    constructor() {
      super();
      AppIndexClass.instanceCount++;
      if (this.isConnected) {
        AppIndexClass.domInstanceCount++;
        instance = this;
      }
    }

    connectedCallback() {
      super.connectedCallback();
      if (!this.isConnected) {
        AppIndexClass.domInstanceCount++;
        instance = this;
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (instance === this) {
        instance = null;
        AppIndexClass.domInstanceCount--;
      }
    }

    firstUpdated() {
      this.initializeApp().catch(console.error);
    }

    updated(changedProperties: Map<PropertyKey, unknown>) {
      super.updated(changedProperties);
     /*   if (changedProperties.size > 0) {
      console.log('State changes:', Array.from(changedProperties.entries()).map(([key, value]) => ({
          property: key,
          oldValue: value,
          newValue: this[key as keyof AppIndexClass]
        })));
      }*/
    }

    private async initializeApp() {
      if (this.canvas && instance === this) {
        try {
          // Set canvas style before initialization
          this.canvas.style.display = 'block';
          this.canvas.style.opacity = '1';

          const { APP3_AppInit, APP3_AppStart } = await import(/* webpackChunkName: "app3-init" */ './app3/app3-Init');

          await APP3_AppInit(this.canvas, instance);

          const { platformXRButtonCheck, platformXRModeCheck } = await import(/* webpackChunkName: "app3-platform" */ './app3/akit/platform/akit-Platform');
          this.xrButtonState = await platformXRButtonCheck();
          this.xrModeState = await platformXRModeCheck();

          await APP3_AppStart();

          await this.handleNavigation('threed');
        } catch (error) {
          // Only log errors if production logging is enabled
          if (APP.enableProductionLogging) {
            console.error('Failed to initialize app:', error);
          }
          // Show error state to user
          this.currentView = 'loading';
          this.contentPromise = Promise.reject(error);
        }
      } else {
        // Only log errors if production logging is enabled
        if (APP.enableProductionLogging) {
          console.error('Canvas not found or not the main instance');
        }
      }
    }

    async handleNavigation(view: 'threed' | 'ar' | 'vr') {
      if (this.currentView !== view) {
        this.currentView = view;
        const { APP_NavigateTo } = await import('./app3/app/ui/app-Nav');
        await APP_NavigateTo(view);
      }
    }

    render() {
      return html`
        ${until(
          this.contentPromise,
          html`
            <div class="loader">
              <sl-spinner></sl-spinner>
              <sl-text>Loading...</sl-text>
            </div>
          `,
        )}
        <canvas></canvas>
        <div class="view-container">${this.renderContent()}</div>

        <sl-drawer placement="start">
          <div class="drawer-content">
            <svg width="64" height="64" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="16" cy="16" r="15" stroke="#8B4513" stroke-width="1.5"/>
              <circle cx="16" cy="16" r="4" fill="#8B4513"/>
            </svg>
            <sl-text size="x-large" style="color: #8B4513;">assimilate.xr</sl-text>
            <sl-qr-code value="https://assimilate.media" error-correction="H" padding="0" size="150"></sl-qr-code>
          </div>
        </sl-drawer>
      `;
    }

    renderContent() {
      switch (this.currentView) {
        case 'ar':
          return html`<app-ar-view
            .xrButtonState=${this.xrButtonState}
            @navigate=${(e: CustomEvent) => this.handleNavigation(e.detail)}
            .toggleDrawer=${this.toggleDrawer.bind(this)}
          ></app-ar-view>`;
        case 'vr':
          return html`<app-vr-view
            .xrButtonState=${this.xrButtonState}
            @navigate=${(e: CustomEvent) => this.handleNavigation(e.detail)}
            .toggleDrawer=${this.toggleDrawer.bind(this)}
          ></app-vr-view>`;
        case 'threed':
          return html`<app-threed-view
            .xrButtonState=${this.xrButtonState}
            @navigate=${(e: CustomEvent) => this.handleNavigation(e.detail)}
            .toggleDrawer=${this.toggleDrawer.bind(this)}
          ></app-threed-view>`;
      }
    }
  }

  customElements.define('app-index', AppIndexClass);
}

class ThreedViewClass extends LitElement {
  @property({ type: Object })
  xrButtonState: XRState = { AR: false, VR: false };

  @property({ type: Function })
  toggleDrawer!: () => void;

  @state()
  private loaderProgress: number = 0;

  private handleNavigation(view: 'threed' | 'ar' | 'vr') {
    this.dispatchEvent(new CustomEvent('navigate', { detail: view }));
  }

  firstUpdated() {
    document.documentElement.requestFullscreen();
    this.updateLoaderProgress();
  }

  private updateLoaderProgress() {
   // const progress = AKIT_StateGet(AKIT_STATE.LOADER_PROGRESS);
    //if (progress && typeof progress.value === 'number') {
    //  this.loaderProgress = progress.value * 100;
    //}
  }

  private switchToAR() {
    this.handleNavigation('ar');
  }

  static styles = css`
    :host {
      display: block;
      height: 100vh;
      width: 100vw;
      position: fixed;
      inset: 0;
    }

    .header {
      position: fixed;
      inset: 0 0 auto 0;
      z-index: var(--sl-z-index-drawer);
      padding: var(--sl-spacing-medium);
    }

    .footer {
      position: fixed;
      inset: auto 0 var(--sl-spacing-2x-large) 0;
      z-index: var(--sl-z-index-drawer);
      display: flex;
      justify-content: center;
    }

    sl-button {
      --background: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
      --border-color: #8B4513;
      --color: #8B4513;
      --box-shadow: none;
      --border-width: 1px;
      --border-radius: 50%;
      --padding: var(--sl-spacing-medium);
      --font-size: var(--sl-font-size-large);
      --border-color-hover: #8B4513;
      --border-color-active: #8B4513;
      --border-color-focus: #8B4513;
      --color-hover: #8B4513;
      --color-active: #8B4513;
      --color-focus: #8B4513;
    }

    sl-button::part(base) {
      background: transparent;
      border-color: #8B4513;
      color: #8B4513;
    }

    sl-button::part(base):hover {
      background: transparent;
      border-color: #8B4513;
      color: #8B4513;
      opacity: 0.8;
    }

    sl-button::part(base):active {
      background: transparent;
      border-color: #8B4513;
      color: #8B4513;
    }

    sl-button::part(base):focus {
      background: transparent;
      border-color: #8B4513;
      color: #8B4513;
    }

    sl-icon-button {
      --background-color: transparent;
      --color: #8B4513;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
    }

    sl-icon-button:hover {
      --background-color: transparent;
      --color: #8B4513;
      opacity: 0.8;
    }

    sl-icon-button[name="x-lg"] {
      --color: #8B4513;
      --background-color: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
    }

    sl-icon-button[name="x-lg"]:hover {
      --color: #8B4513;
      --background-color: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
      opacity: 0.8;
    }

    sl-icon-button[name="x-lg"]:active {
      --background-color: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
    }

    sl-icon-button[name="x-lg"]:focus {
      --background-color: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
    }

    sl-text {
      color: var(--sl-color-neutral-0);
    }

    sl-progress-bar {
      --height: 1px;
      --indicator-color: var(--sl-color-neutral-700);
      --track-color: transparent;
    }

    @media (display-mode: standalone) {
      .header {
        padding-top: var(--sl-spacing-2x-large);
      }
    }
  `;

  render() {
    return html`
      <sl-card class="header">
        <div slot="header" style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium);">
          <sl-progress-bar value="${this.loaderProgress}"></sl-progress-bar>
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <sl-icon-button name="list" label="Menu" @click=${() => this.toggleDrawer()}></sl-icon-button>
            <sl-text size="large" style="color: #8B4513;">assimilate.xr</sl-text>
            <sl-icon-button name="x-lg" label="Close" @click=${() => this.handleNavigation('threed')}></sl-icon-button>
          </div>
        </div>
      </sl-card>

      <div class="footer">
        <sl-button variant="outline" size="large" circle @click=${this.switchToAR}>
          <sl-icon name="box" label="AR View"></sl-icon>
        </sl-button>
      </div>
    `;
  }
}

class ARViewClass extends LitElement {
  @property({ type: Object })
  xrButtonState: XRState = { AR: false, VR: false };

  @property({ type: Function })
  toggleDrawer!: () => void;

  private handleNavigation(view: 'threed' | 'ar' | 'vr') {
    this.dispatchEvent(new CustomEvent('navigate', { detail: view }));
  }

  static styles = css`
    :host {
      display: block;
      height: 100vh;
      width: 100vw;
      position: fixed;
      inset: 0;
    }

    .header {
      position: fixed;
      inset: 0 0 auto 0;
      z-index: var(--sl-z-index-drawer);
      padding: var(--sl-spacing-medium);
    }

    .footer {
      position: fixed;
      inset: auto 0 var(--sl-spacing-2x-large) 0;
      z-index: var(--sl-z-index-drawer);
      display: flex;
      justify-content: center;
    }

    sl-button {
      --background: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
      --border-color: #8B4513;
      --color: #8B4513;
      --box-shadow: none;
      --border-width: 1px;
      --border-radius: 50%;
      --padding: var(--sl-spacing-medium);
      --font-size: var(--sl-font-size-large);
      --border-color-hover: #8B4513;
      --border-color-active: #8B4513;
      --border-color-focus: #8B4513;
      --color-hover: #8B4513;
      --color-active: #8B4513;
      --color-focus: #8B4513;
    }

    sl-button::part(base) {
      background: transparent;
      border-color: #8B4513;
      color: #8B4513;
    }

    sl-button::part(base):hover {
      background: transparent;
      border-color: #8B4513;
      color: #8B4513;
      opacity: 0.8;
    }

    sl-button::part(base):active {
      background: transparent;
      border-color: #8B4513;
      color: #8B4513;
    }

    sl-button::part(base):focus {
      background: transparent;
      border-color: #8B4513;
      color: #8B4513;
    }

    sl-icon-button {
      --background-color: transparent;
      --color: #8B4513;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
    }

    sl-icon-button:hover {
      --background-color: transparent;
      --color: #8B4513;
      opacity: 0.8;
    }

    sl-icon-button[name="x-lg"] {
      --color: #8B4513;
      --background-color: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
    }

    sl-icon-button[name="x-lg"]:hover {
      --color: #8B4513;
      --background-color: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
      opacity: 0.8;
    }

    sl-icon-button[name="x-lg"]:active {
      --background-color: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
    }

    sl-icon-button[name="x-lg"]:focus {
      --background-color: transparent;
      --background-hover: transparent;
      --background-active: transparent;
      --background-focus: transparent;
    }

    sl-text {
      color: var(--sl-color-neutral-0);
    }

    sl-progress-bar {
      --height: 1px;
      --indicator-color: var(--sl-color-neutral-700);
      --track-color: transparent;
    }

    @media (display-mode: standalone) {
      .header {
        padding-top: var(--sl-spacing-2x-large);
      }
    }
  `;

  private switchTo3D() {
    this.handleNavigation('threed');
  }

  private switchToVR() {
    this.handleNavigation('vr');
  }

  render() {
    return html`
      <sl-card class="header">
        <div slot="header" style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium);">
          <sl-progress-bar value="50"></sl-progress-bar>
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <sl-icon-button name="list" label="Menu" @click=${() => this.toggleDrawer()}></sl-icon-button>
            <sl-text size="large" style="color: #8B4513;">assimilate.xr</sl-text>
            <sl-icon-button name="x-lg" label="Close" @click=${() => this.handleNavigation('threed')}></sl-icon-button>
          </div>
        </div>
      </sl-card>

      <div class="footer">
        <sl-button variant="outline" size="large" circle @click=${this.switchTo3D}>
          <sl-icon name="box" label="3D View"></sl-icon>
        </sl-button>
      </div>
    `;
  }
}

class VRViewClass extends LitElement {
  @property({ type: Object })
  xrButtonState: XRState = { AR: false, VR: false };

  @property({ type: Function })
  toggleDrawer!: () => void;

  private handleNavigation(view: 'threed' | 'ar' | 'vr') {
    this.dispatchEvent(new CustomEvent('navigate', { detail: view }));
  }

  firstUpdated() {
    document.documentElement.requestFullscreen();
  }

  private switchToAR() {
    this.handleNavigation('ar');
  }

  private switchTo3D() {
    this.handleNavigation('threed');
  }

  static styles = css`
    :host {
      display: block;
      height: 100vh;
      width: 100vw;
      position: fixed;
      inset: 0;
    }

    .header {
      position: fixed;
      inset: 0 0 auto 0;
      z-index: var(--sl-z-index-drawer);
      padding: var(--sl-spacing-medium);
    }

    .footer {
      position: fixed;
      inset: auto 0 var(--sl-spacing-2x-large) 0;
      z-index: var(--sl-z-index-drawer);
      display: flex;
      justify-content: center;
    }

    @media (display-mode: standalone) {
      .header {
        padding-top: var(--sl-spacing-2x-large);
      }
    }
  `;

  render() {
    return html`
      <sl-card class="header">
        <div slot="header" style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium);">
          <sl-progress-bar value="50"></sl-progress-bar>
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <sl-icon-button name="list" label="Menu" @click=${() => this.toggleDrawer()}></sl-icon-button>
            <sl-text size="large" style="color: #8B4513;">assimilate.xr</sl-text>
            <sl-icon-button name="x-lg" label="Close" @click=${() => this.handleNavigation('threed')}></sl-icon-button>
          </div>
        </div>
      </sl-card>

      <div class="footer">
        <sl-button variant="outline" size="large" circle @click=${this.switchToAR}>
          <sl-icon name="box" label="AR View"></sl-icon>
        </sl-button>
      </div>
    `;
  }
}

if (!customElements.get('app-threed-view')) {
  customElements.define('app-threed-view', ThreedViewClass);
}

if (!customElements.get('app-ar-view')) {
  customElements.define('app-ar-view', ARViewClass);
}

if (!customElements.get('app-vr-view')) {
  customElements.define('app-vr-view', VRViewClass);
}
